/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { jsx } from '@atlaskit/css';
import { ComposableEditor, type EditorNextProps } from '@atlaskit/editor-core/composable-editor';
import { fg } from '@atlaskit/platform-feature-flags';
import type { ContentContext } from '@atlassian/conversation-assistant-store';
import { type SkillIconName } from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import { type Skill } from '@atlassian/rovo-platform-ui-components/skills/types';

import { useSkillsManifest } from '../../../ui/skills-editor-extension/SkillsEditorExtension';

import type { composableEditorChat_GetDirectoryItemsQuery } from './__generated__/composableEditorChat_GetDirectoryItemsQuery.graphql';

export const ComposableEditorWithSkills = ({
	isConversationAgentic,
	promptHasSkill,
	contentContext,
	siteId,
	...props
}: EditorNextProps & {
	siteId: string;
	promptHasSkill: boolean;
	isConversationAgentic: boolean;
	contentContext: ContentContext;
}) => {
	const enableSkills = contentContext !== 'staging-area';

	const skillsResponse = useLazyLoadQuery<composableEditorChat_GetDirectoryItemsQuery>(
		graphql`
			query composableEditorChat_GetDirectoryItemsQuery(
				$first: Int
				$after: String
				$dimensionIds: [ID!]
				$searchKeyword: String
				$relevantFor: [GraphIntegrationDirectoryItemType!]!
				$cloudId: ID!
				$surface: GraphIntegrationSurface
				$shouldFetch: Boolean = false
			) {
				graphIntegration_componentDirectoryItems(
					first: $first
					after: $after
					dimensionIds: $dimensionIds
					searchKeyword: $searchKeyword
					relevantFor: $relevantFor
					cloudId: $cloudId
					surface: $surface
				) @optIn(to: "GraphIntegrationItemsQuery") @include(if: $shouldFetch) {
					edges {
						node {
							__typename
							... on GraphIntegrationSkillDirectoryItem {
								slashCommand
								name
								description
								iconUrl
								integrationKey {
									displayName
									iconUrl
								}
							}
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
					error {
						message
						extensions {
							statusCode
							errorType
						}
					}
				}
			}
		`,
		{
			first: 50,
			relevantFor: ['SKILL'],
			cloudId: siteId,
			surface: 'ROVO',
			shouldFetch: enableSkills && fg('rovo_chat_skills_fe_agg_cutover'),
		},
	)?.graphIntegration_componentDirectoryItems;

	if (skillsResponse && 'error' in skillsResponse && skillsResponse.error) {
		const err = skillsResponse.error;
		const errExtensions = err.extensions
			? `[${err.extensions?.map((ext) => `${ext?.statusCode} - ${ext?.errorType}`)?.join(', ')}] `
			: '';
		throw new Error(
			err.message || errExtensions ? `${errExtensions}${err.message}` : 'Failed to load skills',
		);
	}

	const skillsListAferLoad: Skill[] = React.useMemo(
		() =>
			fg('rovo_chat_skills_fe_agg_cutover')
				? ((
						skillsResponse?.edges &&
						skillsResponse?.edges.map((edge) => {
							const node = edge?.node;
							if (!node || node.__typename !== 'GraphIntegrationSkillDirectoryItem') {
								return undefined;
							}
							return {
								slug: node.slashCommand?.split('/')?.[1] ?? '',
								name: node.name ?? '',
								description: node.description ?? '',
								iconName: node?.iconUrl?.split('name/')?.[1] as SkillIconName,
								product: node.integrationKey?.displayName ?? '',
								productIconUrl: node.integrationKey?.iconUrl ?? '',
							};
						})
					)?.filter((skill) => skill != null) ?? [])
				: [],
		[skillsResponse?.edges],
	);

	const editorExtensionManifest = useSkillsManifest({
		skills: skillsListAferLoad,
		isConversationAgentic,
		promptHasSkill,
		enableSkills,
	});

	return <ComposableEditor extensionProviders={editorExtensionManifest} {...props} />;
};
